import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useId, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InlineButton, Button } from '@skatteetaten/ds-buttons';
import { Panel } from '@skatteetaten/ds-content';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { EditSVGpath, Icon, CheckIcon } from '@skatteetaten/ds-icons';
import { Heading } from '@skatteetaten/ds-typography';
import './StepListStep/styles.css';


const getStepListStepTitleAsDefault = ()=>'h3';
const getStepListStepVariantDefault = ()=>'passive';
const getStepListStepShouldAutoFocusWhenActiveDefault = ()=>true;

var styles = {
    "stepLine_positiveResult": "StepListStep-module_stepLine_positiveResult__0yIhN",
    "stepLine_neutralResult": "StepListStep-module_stepLine_neutralResult__XQ-he",
    "step": "StepListStep-module_step__JTMjK",
    "stepEditButton": "StepListStep-module_stepEditButton__zDziu",
    "step_active": "StepListStep-module_step_active__8zeRX",
    "step_passive": "StepListStep-module_step_passive__-QRXC",
    "step_neutralResult": "StepListStep-module_step_neutralResult__zRm-5",
    "step_positiveResult": "StepListStep-module_step_positiveResult__Uz6y1",
    "stepHeading": "StepListStep-module_stepHeading__6TzJk",
    "stepContent": "StepListStep-module_stepContent__F22-U",
    "stepContent_noButton": "StepListStep-module_stepContent_noButton__DRZ1U",
    "stepContent_positiveResult": "StepListStep-module_stepContent_positiveResult__l1kYI",
    "stepContent_neutralResult": "StepListStep-module_stepContent_neutralResult__V9kIr",
    "stepContent_fullWidth": "StepListStep-module_stepContent_fullWidth__Ec11p",
    "stepSideWrapper": "StepListStep-module_stepSideWrapper__Jxhzx",
    "stepSideWrapper_result": "StepListStep-module_stepSideWrapper_result__Kw0Kz",
    "stepLine": "StepListStep-module_stepLine__Xqp41",
    "stepCircle": "StepListStep-module_stepCircle__3QtmT",
    "stepCircle_passive": "StepListStep-module_stepCircle_passive__uGaTd",
    "stepCircle_active": "StepListStep-module_stepCircle_active__yhWYp",
    "stepCircle_positiveResult": "StepListStep-module_stepCircle_positiveResult__zeQrV",
    "stepCircle_neutralResult": "StepListStep-module_stepCircle_neutralResult__-JJta",
    "stepCircleIcon": "StepListStep-module_stepCircleIcon__LeCAq",
    "resultIntro": "StepListStep-module_resultIntro__jKYu-",
    "buttonWrapper": "StepListStep-module_buttonWrapper__l0Ihq",
    "nextLine": "StepListStep-module_nextLine__kX5K0"
};

const StepListStep = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, editButtonText, introContent, introTitle, introTitleAs, nextButtonProps, nextButtonText, stepNumber, svgPath, svgTitle, title, titleAs = getStepListStepTitleAsDefault(), variant = getStepListStepVariantDefault(), onEdit, onNext, hasResultContentFullWidth, shouldAutoFocusWhenActive = getStepListStepShouldAutoFocusWhenActiveDefault(), children }, ref)=>{
    const { t } = useTranslation('ds_collections', {
        i18n: dsI18n
    });
    const innerRef = useRef(null);
    const generatedId = useId();
    const titleId = `steptitle-${id ?? generatedId}`;
    useEffect(()=>{
        if (shouldAutoFocusWhenActive && innerRef.current && variant === 'active') {
            innerRef.current.focus();
        }
    }, [
        variant,
        shouldAutoFocusWhenActive
    ]);
    const circleClassName = `${styles.stepCircle}
        ${styles[`stepCircle_${variant}`]}`;
    const stepVariantClassName = styles[`step_${variant}`];
    const [stepLineVariantClassName, stepContentVariantClassName] = variant === 'positiveResult' || variant === 'neutralResult' ? [
        styles[`stepLine_${variant}`],
        `${styles[`stepContent_${variant}`]} ${hasResultContentFullWidth ? styles.stepContent_fullWidth : ''}`
    ] : [
        '',
        ''
    ];
    const concatenatedClassName = `${styles.step} ${stepVariantClassName} ${className}`.trim();
    const getCircleContent = ()=>{
        if (svgPath) {
            return /*#__PURE__*/ jsx(Icon, {
                title: svgTitle,
                className: styles.stepCircleIcon,
                svgPath: svgPath
            });
        } else if (variant === 'positiveResult' || variant === 'neutralResult') {
            return /*#__PURE__*/ jsx(CheckIcon, {
                title: t('steplist.ResultStepIconText'),
                className: styles.stepCircleIcon
            });
        }
        return stepNumber;
    };
    const hasStepIntro = ()=>[
            'neutralResult',
            'positiveResult'
        ].includes(variant) && !!(introTitle || introContent);
    return /*#__PURE__*/ jsxs("li", {
        ref: ref,
        id: id,
        className: concatenatedClassName,
        lang: lang,
        "data-testid": dataTestId,
        children: [
            /*#__PURE__*/ jsxs("div", {
                ref: innerRef,
                className: `${styles.stepSideWrapper} ${[
                    'neutralResult',
                    'positiveResult'
                ].includes(variant) ? styles.stepSideWrapper_result : ''}`.trim(),
                id: id ? `${id}-focus-target` : undefined,
                tabIndex: -1,
                "aria-current": variant !== 'passive' ? 'step' : undefined,
                children: [
                    /*#__PURE__*/ jsx("span", {
                        className: circleClassName,
                        children: getCircleContent()
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: `${styles.stepLine} ${stepLineVariantClassName}`.trim()
                    })
                ]
            }),
            /*#__PURE__*/ jsx(Heading, {
                id: titleId,
                className: styles.stepHeading,
                as: titleAs,
                level: 5,
                children: title
            }),
            hasStepIntro() && /*#__PURE__*/ jsx(Panel, {
                className: styles.resultIntro,
                color: variant === 'neutralResult' ? 'ochre' : 'forest',
                variant: 'filled',
                title: introTitle,
                titleAs: introTitleAs,
                children: introContent
            }),
            /*#__PURE__*/ jsxs("div", {
                className: `${styles.stepContent} ${onEdit ? '' : styles.stepContent_noButton} ${stepContentVariantClassName}
          `.trim(),
                children: [
                    /*#__PURE__*/ jsx("div", {
                        children: children
                    }),
                    onEdit && /*#__PURE__*/ jsx(InlineButton, {
                        className: styles.stepEditButton,
                        svgPath: EditSVGpath,
                        ariaDescribedby: titleId,
                        onClick: ()=>{
                            onEdit();
                            setTimeout(()=>{
                                innerRef?.current?.focus();
                            });
                        },
                        children: editButtonText ?? t('steplist.Edit')
                    })
                ]
            }),
            variant === 'active' && onNext && /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: styles.nextLine
                    }),
                    /*#__PURE__*/ jsx("span", {
                        className: styles.buttonWrapper,
                        children: /*#__PURE__*/ jsx(Button, {
                            onClick: onNext,
                            ...nextButtonProps,
                            children: nextButtonText ?? t('steplist.Next')
                        })
                    })
                ]
            })
        ]
    });
});
StepListStep.displayName = 'StepListStep';

export { StepListStep as S, getStepListStepVariantDefault as a, getStepListStepShouldAutoFocusWhenActiveDefault as b, getStepListStepTitleAsDefault as g };
